<template>
  <div v-if="visible" class="find-replace-modal">
    <div 
      class="modal-content"
      :style="{ top: `${modalTop}px`, left: `${modalLeft}px` }"
      ref="modalRef"
    >
      <div class="modal-header" @mousedown="startDrag" @selectstart="false">
        <h3>查找和替换</h3>
        <button @click="close">×</button>
      </div>
      <div class="modal-body">
        <div class="form-item">
          <label>查找：</label>
          <input 
            v-model="findText" 
            @input="handleFindInput"
            placeholder="输入要查找的内容"
            class="input"
          >
        </div>
        <div class="form-item">
          <label>替换为：</label>
          <input 
            v-model="replaceText" 
            placeholder="输入替换后的内容"
            class="input"
          >
        </div>
        <!-- 大小写开关 -->
        <div class="form-item switch-item">
          <label class="switch-title" title="区分字母大小写">大小写：</label>
          <label class="switch-wrap">
            <input 
              type="checkbox" 
              v-model="caseSensitive"
              @change="handleFindInput"
              class="switch"
            >
            <span class="switch-slider"></span>
          </label>
        </div>
        <div class="match-info">找到 {{ matchCount }} 个匹配项</div>
        <div class="button-group">
          <button @click="findPrev" class="btn" :disabled="!matchCount">上一个</button>
          <button @click="findNext" class="btn" :disabled="!matchCount">下一个</button>
          <button @click="replaceCurrent" class="btn primary" :disabled="currentMatchIndex < 0">替换当前</button>
          <button @click="replaceAll" class="btn primary" :disabled="!matchCount">替换所有</button>
        </div>
      </div>
    </div>
    <!-- 遮罩无实际作用（background透明+pointer-events:none），直接删除 -->
  </div>
</template>

<script setup lang="ts">
import { ref, watch, nextTick, onMounted, onUnmounted } from 'vue'

// Props
const props = defineProps<{
  visible: boolean;
  editorInstance: any;
}>()

// Emits
const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'update:visible', value: boolean): void;
}>()

// 核心响应式数据
const findText = ref('')
const replaceText = ref('')
const matchCount = ref(0)
const currentMatchIndex = ref(-1)
const matchPositions = ref<Array<{ from: { line: number; ch: number }, to: { line: number; ch: number } }>>([])
const caseSensitive = ref(false)

// 拖动/定位相关
const modalRef = ref<HTMLDivElement | null>(null)
const modalTop = ref(0)
const modalLeft = ref(0)
const isDragging = ref(false)
let dragStartX = 0 // 非响应式（仅拖动时用）
let dragStartY = 0 // 非响应式（减少ref开销）

// 关闭弹窗
const close = () => {
  emit('update:visible', false)
  emit('close')
}

// 计算居中位置
const calcCenterPos = () => {
  if (!modalRef.value) return
  const { innerWidth: winWidth, innerHeight: winHeight } = window
  const { offsetWidth: modalWidth, offsetHeight: modalHeight } = modalRef.value
  modalTop.value = (winHeight - modalHeight) / 2
  modalLeft.value = (winWidth - modalWidth) / 2
}

// 查找逻辑
const handleFindInput = () => {
  if (!props.editorInstance || !findText.value.trim()) {
    matchCount.value = 0
    matchPositions.value = []
    currentMatchIndex.value = -1
    return
  }

  const cm = props.editorInstance
  const content = cm.getValue()
  const lines = content.split('\n')
  const positions: typeof matchPositions.value = []
  const findStr = findText.value
  const targetFindStr = caseSensitive.value ? findStr : findStr.toLowerCase()

  lines.forEach((line, lineNum) => {
    let lineToCheck = caseSensitive.value ? line : line.toLowerCase()
    let startIndex = 0
    while ((startIndex = lineToCheck.indexOf(targetFindStr, startIndex)) !== -1) {
      positions.push({
        from: { line: lineNum, ch: startIndex },
        to: { line: lineNum, ch: startIndex + findStr.length }
      })
      startIndex += targetFindStr.length
    }
  })

  matchPositions.value = positions
  matchCount.value = positions.length
  currentMatchIndex.value = positions.length > 0 ? 0 : -1

  if (currentMatchIndex.value >= 0) {
    nextTick(() => {
      const pos = positions[0]
      cm.setSelection(pos.from, pos.to)
      cm.scrollIntoView(pos.from, 20)
    })
  }
}

// 查找上一个
const findPrev = () => {
  if (!matchCount.value || !props.editorInstance) return
  currentMatchIndex.value = (currentMatchIndex.value - 1 + matchCount.value) % matchCount.value
  const pos = matchPositions.value[currentMatchIndex.value]
  const cm = props.editorInstance
  cm.setSelection(pos.from, pos.to)
  cm.scrollIntoView(pos.from, 20)
}

// 查找下一个
const findNext = () => {
  if (!matchCount.value || !props.editorInstance) return
  currentMatchIndex.value = (currentMatchIndex.value + 1) % matchCount.value
  const pos = matchPositions.value[currentMatchIndex.value]
  const cm = props.editorInstance
  cm.setSelection(pos.from, pos.to)
  cm.scrollIntoView(pos.from, 20)
}

// 替换当前
const replaceCurrent = () => {
  if (currentMatchIndex.value < 0 || !props.editorInstance || !findText.value) return
  const cm = props.editorInstance
  const pos = matchPositions.value[currentMatchIndex.value]
  cm.replaceRange(replaceText.value, pos.from, pos.to)
  nextTick(handleFindInput)
}

// 替换所有
const replaceAll = () => {
  if (!matchCount.value || !props.editorInstance || !findText.value) return
  const cm = props.editorInstance
  let content = cm.getValue()

  if (caseSensitive.value) {
    content = content.split(findText.value).join(replaceText.value)
  } else {
    const reg = new RegExp(findText.value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi')
    content = content.replace(reg, replaceText.value)
  }

  cm.setValue(content)
  alert(`成功替换 ${matchCount.value} 个匹配项`)
  handleFindInput()
}

// 拖动事件
const startDrag = (e: MouseEvent) => {
  if (!modalRef.value) return
  isDragging.value = true
  dragStartX = e.clientX - modalRef.value.offsetLeft
  dragStartY = e.clientY - modalRef.value.offsetTop
  
  document.addEventListener('mousemove', onDrag)
  document.addEventListener('mouseup', stopDrag)
  e.preventDefault()
}

const onDrag = (e: MouseEvent) => {
  if (!isDragging.value || !modalRef.value) return
  const { offsetWidth: modalWidth, offsetHeight: modalHeight } = modalRef.value
  modalLeft.value = e.clientX - dragStartX
  modalTop.value = e.clientY - dragStartY
  
  // 边界限制
  const maxX = window.innerWidth - modalWidth - 20
  const maxY = window.innerHeight - modalHeight - 20
  modalLeft.value = Math.max(20, Math.min(maxX, modalLeft.value))
  modalTop.value = Math.max(20, Math.min(maxY, modalTop.value))
}

const stopDrag = () => {
  isDragging.value = false
  document.removeEventListener('mousemove', onDrag)
  document.removeEventListener('mouseup', stopDrag)
}

// 窗口大小变化
const handleResize = () => {
  if (props.visible && !isDragging.value) calcCenterPos()
}

// 监听显隐
watch(() => props.visible, (val) => {
  if (val) nextTick(() => { calcCenterPos(); handleFindInput() })
})

// 监听大小写开关
watch(caseSensitive, handleFindInput)

// 生命周期
onMounted(() => {
  nextTick(() => { if (props.visible) calcCenterPos() })
  window.addEventListener('resize', handleResize)
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
  // 防止内存泄漏：移除残留的拖动事件
  document.removeEventListener('mousemove', onDrag)
  document.removeEventListener('mouseup', stopDrag)
})
</script>

<style scoped>
.find-replace-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 9999;
  pointer-events: none;
}

.modal-content {
  position: absolute;
  background: #fff;
  border-radius: 8px;
  width: 400px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  pointer-events: auto;
}

.modal-header {
  padding: 12px 16px;
  border-bottom: 1px solid #e8e8e8;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: move;
  user-select: none;
}

.modal-header h3 {
  margin: 0;
  font-size: 16px;
  color: #333;
}

.modal-header button {
  background: transparent;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #999;
  width: 30px;
  height: 30px;
  line-height: 30px;
  text-align: center;
}

.modal-body {
  padding: 16px;
}

.form-item {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
}

.form-item label {
  width: 60px;
  font-size: 14px;
  color: #666;
}

.input {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 14px;
}

/* 开关样式 */
.switch-item {
  margin-bottom: 8px;
  padding: 0 2px;
}

.switch-title {
  font-size: 14px;
  color: #666;
  width: auto !important;
  flex: none;
}

.switch-wrap {
  display: flex;
  align-items: center;
  cursor: pointer;
  flex: none;
}

.switch {
  appearance: none;
  width: 40px;
  height: 20px;
  border-radius: 10px;
  background: #d9d9d9;
  position: relative;
  cursor: pointer;
  transition: background 0.3s;
}

.switch:checked {
  background: #1890ff;
}

.switch::after {
  content: '';
  position: absolute;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #fff;
  top: 1px;
  left: 1px;
  transition: left 0.3s;
}

.switch:checked::after {
  left: 21px;
}

.match-info {
  font-size: 12px;
  color: #999;
  margin-bottom: 16px;
}

.button-group {
  display: flex;
  gap: 8px;
}

.btn {
  padding: 8px 16px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
  font-size: 14px;
}

.btn:disabled {
  cursor: not-allowed;
}

.btn.primary {
  background: #1890ff;
  color: #fff;
  border-color: #1890ff;
}
</style>